/**
 * ServiceCustomerDetailModal — centered tabbed modal showing full
 * service-customer details. Matches the styling of CustomerDetailsModal
 * (sm:max-w-[850px], gradient header, sticky tabs).
 *
 * Tabs:
 *   1. Overview     — Customer + Vehicle + status snapshot
 *   2. Service      — Package, slots, mechanic, media
 *   3. Payment      — Pricing, payment status, Razorpay, invoice
 *   4. Audit Log    — Unified status_history + activities timeline
 *
 * Replaces the earlier slide-over drawer.
 * Added 2026-06-04.
 */
import React, { useEffect, useState } from 'react';
import { customersApi } from '@/services/api';
import { formatCurrency } from '@/utils/currency';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  X, Loader2, Phone, Mail, MapPin, Car, Package, IndianRupee,
  Calendar, Wrench, User, FileText, ExternalLink,
  Clock, Activity, CheckCircle, AlertCircle, CreditCard, Image as ImageIcon,
} from 'lucide-react';

const STATUS_STYLE = {
  COMPLETED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  BOOKED: 'bg-blue-100 text-blue-700 border-blue-200',
  CONFIRMED: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  PAYMENT_LINK_SENT: 'bg-amber-100 text-amber-700 border-amber-200',
  NEW: 'bg-slate-100 text-slate-700 border-slate-200',
};
const PAY_STYLE = {
  PAID: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  PARTIAL_PAID: 'bg-blue-100 text-blue-700 border-blue-200',
  LINK_SENT: 'bg-amber-100 text-amber-700 border-amber-200',
  PENDING: 'bg-slate-100 text-slate-700 border-slate-200',
  EXPIRED: 'bg-rose-100 text-rose-700 border-rose-200',
};

const StatusBadge = ({ value, kind = 'status' }) => {
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  const cfg = kind === 'pay' ? PAY_STYLE : STATUS_STYLE;
  const cls = cfg[value] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>
      {value}
    </span>
  );
};

const KV = ({ label, value, mono = false, valueClassName = '' }) => (
  <div className="flex justify-between items-start gap-3 py-1.5 border-b border-gray-100 last:border-0">
    <span className="text-xs text-gray-500 shrink-0 pt-0.5">{label}</span>
    <span className={`text-sm font-medium text-right text-gray-900 ${mono ? 'font-mono text-xs' : ''} ${valueClassName}`}>
      {value === null || value === undefined || value === '' ? <span className="text-gray-400">—</span> : value}
    </span>
  </div>
);

const SectionCard = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-white border rounded-lg overflow-hidden" data-testid={testid}>
    <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-2.5 border-b flex items-center gap-2">
      <Icon className="h-4 w-4 text-gray-500" />
      <span className="text-xs font-bold uppercase tracking-wider text-gray-700">{title}</span>
    </div>
    <div className="px-4 py-3">
      {children}
    </div>
  </div>
);

const fmtTs = (ts) => {
  if (!ts) return null;
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
};

const fmtDateOnly = (ts) => {
  if (!ts) return null;
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ts; }
};

const fmtSlot = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
};

export const ServiceCustomerDetailModal = ({ serviceLeadId, isOpen, onClose, countryCode }) => {
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!isOpen || !serviceLeadId) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setActiveTab('overview');
        const res = await customersApi.getServiceCustomerDetail(serviceLeadId);
        if (alive) setDoc(res.data);
      } catch (e) {
        toast.error('Failed to load service customer details');
        onClose?.();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [serviceLeadId, isOpen, onClose]);

  if (!isOpen) return null;

  // Audit log derived in render — entries already sorted by backend
  const audit = doc?.audit_log || [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        className="sm:max-w-[900px] max-h-[90vh] p-0 overflow-hidden flex flex-col"
        data-testid="service-customer-modal"
      >
        <DialogTitle className="sr-only">Service Customer Details</DialogTitle>

        {/* Gradient header — matches CustomerDetailsModal styling */}
        <div className="bg-gradient-to-r from-[#2E3192] to-[#1BBBCE] text-white px-6 py-4 flex justify-between items-start shrink-0">
          {loading || !doc ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading...</span>
            </div>
          ) : (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-bold truncate" data-testid="modal-customer-name">
                  {doc.customer_name || 'Service Customer'}
                </h2>
                <StatusBadge value={doc.status} />
                <StatusBadge value={doc.payment_status} kind="pay" />
              </div>
              <div className="mt-1 flex items-center gap-3 text-sm text-white/80 flex-wrap">
                {doc.mobile && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" /> {doc.mobile}
                  </span>
                )}
                {doc.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {doc.city}
                  </span>
                )}
                <span className="text-xs text-white/60 font-mono">{doc.id}</span>
              </div>
            </div>
          )}
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg shrink-0 ml-3"
            data-testid="service-modal-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        {!loading && doc && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <div className="bg-white border-b sticky top-0 z-10 shrink-0 px-6">
              <TabsList className="bg-transparent h-auto p-0 gap-1">
                <TabsTrigger
                  value="overview"
                  data-testid="modal-tab-overview"
                  className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:border-blue-600 border-b-2 border-transparent rounded-none px-4 py-3"
                >
                  <User className="h-4 w-4 mr-2" /> Overview
                </TabsTrigger>
                <TabsTrigger
                  value="service"
                  data-testid="modal-tab-service"
                  className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:border-blue-600 border-b-2 border-transparent rounded-none px-4 py-3"
                >
                  <Wrench className="h-4 w-4 mr-2" /> Service
                </TabsTrigger>
                <TabsTrigger
                  value="payment"
                  data-testid="modal-tab-payment"
                  className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:border-blue-600 border-b-2 border-transparent rounded-none px-4 py-3"
                >
                  <CreditCard className="h-4 w-4 mr-2" /> Payment
                </TabsTrigger>
                <TabsTrigger
                  value="audit"
                  data-testid="modal-tab-audit"
                  className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:border-blue-600 border-b-2 border-transparent rounded-none px-4 py-3"
                >
                  <Activity className="h-4 w-4 mr-2" /> Audit Log
                  {audit.length > 0 && (
                    <span className="ml-2 bg-gray-200 text-gray-700 text-xs px-1.5 py-0.5 rounded-full">
                      {audit.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5">
              {/* ───────── OVERVIEW ───────── */}
              <TabsContent value="overview" className="m-0 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SectionCard icon={User} title="Customer" testid="overview-customer">
                    <KV label="Name" value={doc.customer_name} />
                    <KV label="Mobile" value={doc.mobile} mono />
                    <KV label="Email" value={doc.email} />
                    <KV label="City" value={doc.city} />
                    <KV label="Address" value={doc.address} />
                    <KV label="Source" value={doc.source} />
                  </SectionCard>

                  <SectionCard icon={Car} title="Vehicle" testid="overview-vehicle">
                    <KV label="Brand" value={doc.vehicle?.brand} />
                    <KV label="Model" value={doc.vehicle?.model} />
                    <KV label="Variant" value={doc.vehicle?.variant} />
                    <KV label="Registration" value={doc.vehicle?.registration} mono />
                    <KV label="Year" value={doc.vehicle?.year} />
                    <KV label="Fuel" value={doc.vehicle?.fuel_type} />
                    <KV label="Body class" value={doc.vehicle?.body_class} />
                    <KV label="KMs driven" value={doc.vehicle?.kms_driven?.toLocaleString()} />
                  </SectionCard>
                </div>

                <SectionCard icon={CheckCircle} title="Snapshot" testid="overview-snapshot">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Service Status</p>
                      <div className="mt-1"><StatusBadge value={doc.status} /></div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Payment</p>
                      <div className="mt-1">
                        {(() => {
                          const grand = Number(doc.final_price || 0) + Number(doc.spare_parts_total || 0);
                          const paid = Number(doc.paid_amount ?? doc.advance_amount ?? 0);
                          const lbl = paid <= 0 ? 'Not Paid' : (grand > 0 && paid + 0.5 >= grand ? 'Fully Paid' : 'Advance Paid');
                          const cls = lbl === 'Fully Paid'
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : lbl === 'Advance Paid'
                              ? 'bg-blue-100 text-blue-700 border-blue-200'
                              : 'bg-slate-100 text-slate-700 border-slate-200';
                          return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}>{lbl}</span>;
                        })()}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Grand Total</p>
                      <p className="font-bold text-emerald-700 mt-1">
                        {formatCurrency((Number(doc.final_price || 0) + Number(doc.spare_parts_total || 0)), countryCode)}
                      </p>
                      {Number(doc.spare_parts_total || 0) > 0 && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          incl. parts {formatCurrency(doc.spare_parts_total, countryCode)}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Paid</p>
                      <p className="font-bold text-blue-700 mt-1">
                        {formatCurrency((doc.paid_amount ?? doc.advance_amount ?? 0), countryCode)}
                      </p>
                    </div>
                  </div>
                  {(doc.confirmed_slot || doc.preferred_slot) && (
                    <div className="mt-4 pt-3 border-t flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-500">Slot:</span>
                      <span className="font-semibold text-gray-900">
                        {fmtSlot(doc.confirmed_slot || doc.preferred_slot)}
                      </span>
                      {doc.confirmed_slot ? (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded bg-indigo-100 text-indigo-700">
                          CONFIRMED
                        </span>
                      ) : (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700">
                          PREFERRED
                        </span>
                      )}
                    </div>
                  )}
                </SectionCard>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SectionCard icon={Calendar} title="Timestamps" testid="overview-timestamps">
                    <KV label="Created" value={fmtTs(doc.created_at)} />
                    <KV label="Updated" value={fmtTs(doc.updated_at)} />
                    <KV label="Confirmed Slot" value={fmtSlot(doc.confirmed_slot)} />
                    <KV label="Service Started" value={fmtTs(doc.actual_start_at)} />
                    <KV label="Service Completed" value={fmtTs(doc.actual_end_at)} />
                  </SectionCard>

                  <SectionCard icon={Wrench} title="Assignment" testid="overview-assignment">
                    <KV label="Sales Rep" value={doc.assigned_to_name} valueClassName="text-blue-700" />
                    <KV label="Mechanic" value={doc.assigned_mechanic_name} valueClassName="text-blue-700" />
                    <KV label="Mechanic Acceptance" value={doc.mechanic_acceptance_status} />
                  </SectionCard>
                </div>

                {doc.notes && (
                  <SectionCard icon={FileText} title="Notes">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{doc.notes}</p>
                  </SectionCard>
                )}
              </TabsContent>

              {/* ───────── SERVICE ───────── */}
              <TabsContent value="service" className="m-0 space-y-4">
                <SectionCard icon={Package} title="Service Package" testid="service-package">
                  <KV label="Package" value={doc.package_name} valueClassName="text-blue-700 font-semibold" />
                  <KV label="Category" value={doc.package_category} />
                  <KV label="Package ID" value={doc.package_id} mono />
                  <KV label="Source" value={doc.source} />
                  <KV label="GST applicable" value={doc.gst_applicable ? 'Yes' : 'No'} />
                </SectionCard>

                <SectionCard icon={Calendar} title="Slots & Service Timeline" testid="service-slots">
                  <KV label="Preferred Slot" value={fmtSlot(doc.preferred_slot)} />
                  <KV label="Confirmed Slot" value={fmtSlot(doc.confirmed_slot)} />
                  <KV label="Service Started" value={fmtTs(doc.actual_start_at)} />
                  <KV label="Service Completed" value={fmtTs(doc.actual_end_at)} />
                </SectionCard>

                <SectionCard icon={Wrench} title="Mechanic" testid="service-mechanic">
                  <KV label="Mechanic Name" value={doc.assigned_mechanic_name} valueClassName="text-blue-700 font-semibold" />
                  <KV label="Mechanic ID" value={doc.assigned_mechanic_id} mono />
                  <KV label="Acceptance" value={doc.mechanic_acceptance_status} />
                  <KV label="Sales Rep" value={doc.assigned_to_name} valueClassName="text-blue-700" />
                </SectionCard>

                {Array.isArray(doc.extras) && doc.extras.length > 0 && (
                  <SectionCard icon={Package} title={`Extras (${doc.extras.length})`} testid="service-extras">
                    <ul className="space-y-2">
                      {doc.extras.map((x, i) => (
                        <li key={i} className="flex justify-between items-center text-sm border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                          <span className="text-gray-700">{x.name || x.title || JSON.stringify(x)}</span>
                          {x.price !== undefined && (
                            <span className="font-medium text-gray-900">
                              {formatCurrency(x.price, countryCode)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </SectionCard>
                )}

                {/* Media & Documents */}
                {(doc.warranty_card_url || doc.obd_scan || (Array.isArray(doc.service_photos) && doc.service_photos.length > 0) || doc.inspection_report) && (
                  <SectionCard icon={ImageIcon} title="Service Media" testid="service-media">
                    <div className="space-y-2 text-sm">
                      {doc.warranty_card_url && (
                        <a href={doc.warranty_card_url} target="_blank" rel="noopener noreferrer"
                           className="flex items-center justify-between gap-2 text-blue-600 hover:underline px-2 py-1.5 hover:bg-blue-50 rounded">
                          <span className="flex items-center gap-2">
                            <FileText className="h-4 w-4" /> Warranty Card
                          </span>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      {doc.obd_scan && (
                        <div className="text-xs text-gray-600 px-2">✓ OBD scan available</div>
                      )}
                      {Array.isArray(doc.service_photos) && doc.service_photos.length > 0 && (
                        <div className="text-xs text-gray-600 px-2">
                          ✓ {doc.service_photos.length} service photo(s) attached
                        </div>
                      )}
                      {doc.inspection_report && (
                        <div className="text-xs text-gray-600 px-2">✓ Linked inspection report</div>
                      )}
                    </div>
                  </SectionCard>
                )}
              </TabsContent>

              {/* ───────── PAYMENT ───────── */}
              <TabsContent value="payment" className="m-0 space-y-4">
                {(() => {
                  // Derive truth from raw fields (in case payment_status hasn't been
                  // updated by the webhook yet — rep needs to see ground truth).
                  // Grand total = service package final + spare parts total.
                  const packagePrice = Number(doc.final_price || 0);
                  const sparePartsTotal = Number(doc.spare_parts_total || 0);
                  const finalPrice = packagePrice + sparePartsTotal;
                  // Server may expose either `paid_amount` (sum) or only `advance_amount`.
                  const paidAmount = Number(
                    doc.paid_amount !== undefined && doc.paid_amount !== null
                      ? doc.paid_amount
                      : (doc.advance_amount || 0)
                  );
                  const balanceFromField = doc.balance_amount !== undefined && doc.balance_amount !== null
                    ? Number(doc.balance_amount)
                    : null;
                  const balanceComputed = Math.max(0, finalPrice - paidAmount);
                  const balanceDue = balanceFromField !== null ? balanceFromField : balanceComputed;
                  const isFullyPaid = finalPrice > 0 && balanceDue <= 0.5;
                  const isPartial = !isFullyPaid && paidAmount > 0;
                  // Rep-friendly derived label (replaces backend internal codes).
                  const derivedLabel = isFullyPaid
                    ? 'Fully Paid'
                    : isPartial
                      ? 'Advance Paid'
                      : 'Not Paid';
                  const derivedTone = isFullyPaid ? 'paid' : isPartial ? 'partial' : 'pending';

                  const txns = Array.isArray(doc.payment_transactions) ? doc.payment_transactions : [];
                  const addlLinks = Array.isArray(doc.additional_payment_links) ? doc.additional_payment_links : [];

                  return (
                    <>
                      <SectionCard icon={IndianRupee} title="Pricing Breakdown" testid="payment-pricing">
                        <KV label="Service Package Price" value={formatCurrency(packagePrice, countryCode)} />
                        {sparePartsTotal > 0 && (
                          <KV label={`Spare Parts (${Array.isArray(doc.spare_parts) ? doc.spare_parts.length : ''})`} value={formatCurrency(sparePartsTotal, countryCode)} />
                        )}
                        {doc.discount > 0 && (
                          <KV label="Discount" value={`− ${formatCurrency(doc.discount, countryCode)}`} valueClassName="text-rose-600" />
                        )}
                        <KV label="GST Applicable" value={doc.gst_applicable ? 'Yes' : 'No'} />
                        <KV label="Grand Total" value={formatCurrency(finalPrice, countryCode)} valueClassName="text-emerald-700 font-bold text-base" />
                        <KV label="Total Paid" value={formatCurrency(paidAmount, countryCode)} valueClassName="text-blue-700 font-bold" />
                        <KV
                          label="Balance Due"
                          value={formatCurrency(balanceDue, countryCode)}
                          valueClassName={balanceDue > 0.5 ? 'text-amber-700 font-bold' : 'text-emerald-600 font-bold'}
                        />
                        {isFullyPaid && (
                          <div className="mt-2 pt-2 border-t flex items-center gap-2 text-sm text-emerald-700">
                            <CheckCircle className="h-4 w-4" />
                            <span className="font-semibold">Fully paid — balance settled</span>
                          </div>
                        )}
                      </SectionCard>

                      <SectionCard icon={CreditCard} title="Payment Status" testid="payment-status">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-gray-500">Payment Progress</span>
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              derivedTone === 'paid'
                                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                : derivedTone === 'partial'
                                  ? 'bg-blue-100 text-blue-700 border-blue-200'
                                  : 'bg-slate-100 text-slate-700 border-slate-200'
                            }`}>
                              {derivedLabel}
                            </span>
                          </div>
                          <KV label="Payment Method" value={doc.payment_method} />
                          {doc.razorpay_paid_at && (
                            <KV label="First Paid At" value={fmtTs(doc.razorpay_paid_at)} />
                          )}
                        </div>
                        {doc.razorpay_link_url && balanceDue > 0.5 && (
                          <div className="mt-3 pt-3 border-t">
                            <a
                              href={doc.razorpay_link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline px-3 py-2 bg-blue-50 rounded-lg hover:bg-blue-100"
                              data-testid="payment-rzp-link"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Open Razorpay Payment Link
                            </a>
                          </div>
                        )}
                      </SectionCard>

                      {/* Balance / Additional payment links — surfaces extra Razorpay
                          links generated for the remaining balance after the advance. */}
                      {addlLinks.length > 0 && (
                        <SectionCard icon={CreditCard} title={`Additional / Balance Payments (${addlLinks.length})`} testid="payment-additional-links">
                          <div className="space-y-3">
                            {addlLinks.map((lnk, i) => {
                              const isPaid = (lnk.payment_status || '').toUpperCase() === 'PAID';
                              return (
                                <div key={lnk.id || i} className="border rounded-lg p-3 bg-white" data-testid={`additional-link-${i}`}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="text-sm font-semibold text-gray-900">
                                        {formatCurrency(lnk.amount, countryCode)}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-0.5">
                                        {lnk.description || 'Balance / Additional payment'}
                                      </div>
                                      <div className="text-[11px] text-gray-400 mt-1">
                                        Created {fmtTs(lnk.created_at)}
                                        {lnk.created_by ? ` · by ${lnk.created_by}` : ''}
                                      </div>
                                    </div>
                                    <StatusBadge
                                      value={lnk.payment_status || 'PENDING'}
                                      kind="pay"
                                    />
                                  </div>
                                  {lnk.paid_at && (
                                    <div className="mt-2 pt-2 border-t flex items-center gap-1.5 text-xs text-emerald-700">
                                      <CheckCircle className="h-3.5 w-3.5" />
                                      <span>Paid at {fmtTs(lnk.paid_at)}</span>
                                    </div>
                                  )}
                                  {!isPaid && lnk.razorpay_link_url && (
                                    <div className="mt-2 pt-2 border-t">
                                      <a
                                        href={lnk.razorpay_link_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                      >
                                        <ExternalLink className="h-3 w-3" /> Open Link
                                      </a>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </SectionCard>
                      )}

                      {/* Transaction history — every successful Razorpay payment. */}
                      {txns.length > 0 && (
                        <SectionCard icon={CheckCircle} title={`Payment Transactions (${txns.length})`} testid="payment-transactions">
                          <div className="space-y-2">
                            {txns.map((t, i) => (
                              <div
                                key={t.id || i}
                                className="flex items-center justify-between border-b border-gray-100 last:border-0 py-2 text-sm"
                                data-testid={`payment-txn-${i}`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                                  <div className="min-w-0">
                                    <div className="font-semibold text-gray-900">
                                      {formatCurrency(t.amount, countryCode)}
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                      {fmtTs(t.created_at)}
                                      {t.payment_method ? ` · ${t.payment_method}` : ''}
                                    </div>
                                    {t.razorpay_payment_id && (
                                      <div className="text-[10px] text-gray-400 font-mono mt-0.5 truncate">
                                        {t.razorpay_payment_id}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <StatusBadge
                                  value={(t.status || 'completed').toUpperCase() === 'COMPLETED' ? 'PAID' : (t.status || 'pending').toUpperCase()}
                                  kind="pay"
                                />
                              </div>
                            ))}
                          </div>
                        </SectionCard>
                      )}

                      {doc.gst_invoice_url && (
                        <SectionCard icon={FileText} title="GST Invoice" testid="payment-invoice">
                          <a
                            href={doc.gst_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline px-3 py-2 bg-blue-50 rounded-lg hover:bg-blue-100"
                          >
                            <FileText className="h-4 w-4" /> View GST Invoice <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </SectionCard>
                      )}
                    </>
                  );
                })()}
              </TabsContent>

              {/* ───────── AUDIT LOG ───────── */}
              <TabsContent value="audit" className="m-0">
                <SectionCard icon={Activity} title={`Audit Log (${audit.length})`} testid="audit-section">
                  {audit.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      No audit entries
                    </div>
                  ) : (
                    <ol className="relative border-l-2 border-blue-200 ml-2 pl-5 space-y-4" data-testid="audit-timeline">
                      {audit.map((entry, i) => (
                        <li
                          key={i}
                          className="relative"
                          data-testid={`audit-entry-${i}`}
                        >
                          <span className={`absolute -left-[27px] top-1 h-4 w-4 rounded-full border-2 border-white shadow ${
                            entry.kind === 'status' ? 'bg-blue-500' : 'bg-gray-400'
                          }`} />
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {fmtTs(entry.at)}
                          </div>
                          <div className="text-sm font-semibold text-gray-900 mt-0.5">
                            {entry.kind === 'status' && entry.from
                              ? <>Status: <span className="text-gray-500">{entry.from}</span> → <span className="text-blue-700">{entry.to}</span></>
                              : entry.kind === 'status'
                                ? <>Status set: <span className="text-blue-700">{entry.to}</span></>
                                : <span className="capitalize">{entry.kind || 'activity'}</span>}
                          </div>
                          {entry.note && (
                            <div className="text-sm text-gray-700 mt-1 bg-gray-50 px-2 py-1 rounded">
                              {entry.note}
                            </div>
                          )}
                          {entry.by && (
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <User className="h-3 w-3" /> by <span className="font-medium">{entry.by}</span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </SectionCard>
              </TabsContent>
            </div>
          </Tabs>
        )}

        {loading && (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceCustomerDetailModal;
